import React from 'react'
import { Controller, useFormContext, useWatch } from 'react-hook-form'
import { TabButton, TabButtonGroupBase } from 'src/Components/TabButton'
import { TabButtonGroupBaseProps } from 'src/Components/TabButton/TabButtonGroupBase'

interface Option {
  label: React.ReactNode
  value: string | number | boolean
  isDisabled?: boolean
  tooltip?: string
}

type Props = {
  name: string
  onChange?: (value: string | number | boolean) => void
  options: Option[]
} & Omit<TabButtonGroupBaseProps, 'children' | 'onChange'>

/**
 * MUI TabButton with react hook form
 */
const TabButtonGroup: React.FC<Props> = ({ name, options, onChange, ...props }) => {
  const { control, setValue } = useFormContext()
  const value = useWatch({ name })

  const selectedOption = options.find(option => option?.value === value)
  const defaultTabIndex = selectedOption ? options.indexOf(selectedOption) : undefined
  const matchedIndex = options.findIndex(option => option.value === value)
  const selectedTabIndex = matchedIndex >= 0 ? matchedIndex : undefined

  const handleOnClick = (name: string, optionValue: string | number | boolean): void => {
    setValue(name, optionValue, { shouldDirty: true, shouldTouch: true })
    onChange?.(optionValue)
  }

  return (
    <Controller
      name={name}
      control={control}
      render={() => (
        <TabButtonGroupBase defaultTabIndex={defaultTabIndex} tabIndex={selectedTabIndex} {...props}>
          {
            options.slice(0, 3).map(({ isDisabled, tooltip, value, label }) => (
              <TabButton key={`${label}-${value}`} isDisabled={isDisabled} tooltip={tooltip} onClick={() => handleOnClick(name, value)}>
                {label}
              </TabButton>
            )) as TabButtonGroupBaseProps['children']
          }
        </TabButtonGroupBase>
      )}
    />
  )
}

TabButtonGroup.displayName = 'TabButtonGroup'

export default TabButtonGroup
